import WorthyApi from 'lib/api/worthyApi';
import rootStore from 'reducers';
import GA from 'lib/ga';
import {hideLoader, showLoader} from 'actions/loader';
import { SUPER_EXPENSIVE } from '../helpers/ApplicationHelper';

export const ITEM = Symbol('ITEM');
export const NEW_ITEM = Symbol('NEW_ITEM');
export const ADDITIONAL_ITEM = Symbol('ADDITIONAL_ITEM');
export const ITEM_GENERATE_ESTIMATION = Symbol('ITEM_GENERATE_ESTIMATION');
export const ITEM_ESTIMATION = Symbol('ITEM_ESTIMATION');
export const SHIPPING_LABEL = Symbol('SHIPPING_LABEL');
export const ONLY_SHIPPING_LABEL = Symbol('ONLY_SHIPPING_LABEL');
export const PENDING_AUCTION = Symbol('PENDING_AUCTION');
export const EVALUATION_READY = Symbol('EVALUATION_READY');
export const UPDATE_HV_CUI_LAST_MESSAGE_KEY = Symbol('UPDATE_HV_CUI_LAST_MESSAGE_KEY');
export const NO_ACTION = Symbol('NO_ACTION');
export const CONFIRM_SHIPBACK_ADDRESS = 'CONFIRM_SHIPBACK_ADDRESS';
export const CONFIRM_SHIPBACK_ADDRESS_REJECTED = 'CONFIRM_SHIPBACK_ADDRESS_REJECTED';
export const UPDATE_SHIPBACK_ADDRESSES = 'UPDATE_SHIPBACK_ADDRESSES';
export const UPDATE_SHIPBACK_DESTINATION = 'UPDATE_SHIPBACK_DESTINATION';

// don't get the same item in parallel more than once.
const gettingItems = {};

// we want to avoid sending the generate estimation function more than once.
// the boolean is for the regular flow. the local storage key is for a case of a timely
// refresh. A race still exists when someone will hit refresh after the api call happened
// and before it was returned, but this is less frequent.
let generateEstimationCallSent = false;
const ESTIMATION_GENERATED_PREFIX_KEY = 'ESTIMATION_GENERATED_PREFIX_KEY';

export function createItem(params) {
    rootStore.dispatch(showLoader(false));

    let request = WorthyApi.submitItem(params).then((response) => {
        rootStore.dispatch(hideLoader(false));
        return response;
    }).catch((error) => {
        rootStore.dispatch(hideLoader(false));
        throw error;
    });

    return {
        type: NEW_ITEM,
        payload: request
    };
}

export function getItem (id, initLoader = false, params={}, retry = false){
    let _showLoader = ()=>{if(initLoader) rootStore.dispatch(showLoader());};
    let _hideLoader = ()=>{if(initLoader) rootStore.dispatch(hideLoader());};

    // don't get item twice
    if (gettingItems[id] === true && !retry) {
        return {
            type: NO_ACTION,
            payload: null,
        };
    }

    gettingItems[id] = false;

    _showLoader();
    let fc;
    if (parseInt(params['addressWaiting']) === 1) {
        fc = WorthyApi.acceptItemEstimationByPublicID.bind(WorthyApi);
    } else {
        fc = WorthyApi.getItemByPublicID.bind(WorthyApi);
    }
    let request =
        fc(id).then((res) => {
            gettingItems[id] = true;
            let item = res.data.item;
            if (!generateEstimationIfNeeded(item)) {
                _hideLoader();
            }
            return res;
        })
            .catch((error) => {
                gettingItems[id] = false;
                _hideLoader();
                throw error;
            });

    return {
        type:       ITEM,
        payload:    request
    };
}

export async  function updateItem(itemId) {
    const { data } = await WorthyApi.getItemByPublicID(itemId);

    return {
        type: ITEM,
        payload: { data },
    };
}

export function delayedItemUpdate(dispatch) {
    return async function update(item, fieldToCompare, options = {}) {
        const { retries = 10, delay = 1000, called = false } = options;
        try {
            const { data } = await WorthyApi.getItemByPublicID(item.id);
            const newItem = data.item;

            if (item[fieldToCompare] !== newItem[fieldToCompare]) {
                return dispatch({
                    type: ITEM,
                    payload: { data },
                });
            }
            if (!called) {
                dispatch({
                    type: ITEM,
                    payload: { data },
                });
            }
        } catch (error) {
            window?.rollbar?.error(
                `Failed to fetch item details for item ID ${item.id}`
            );
        }

        if (retries === 0) {
            window?.rollbar?.error(
                `Auto grade did not update in 10 seconds for item ${item.id}`
            );
            return;
        }

        await new Promise((resolve) => {
            setTimeout(resolve, delay);
        });

        return update.bind(this)(item, fieldToCompare, {
            ...options,
            retries: retries - 1,
            called: true,
        });
    };
}

export function clearItem() {
        return {
            type:       ITEM,
            payload: {
                data: {
                    item: null
                }
            }
        };
}

export async function confirmShipBackAddress(itemId, params) {
    try {
        const { data } = await WorthyApi.postShipBackAddress(itemId, params);

        if (!data || !data.item) {
            throw new Error('No item was returned on confirm shipback address');
        }

    return {
      type: CONFIRM_SHIPBACK_ADDRESS,
      payload: {
        item: data.item,
      },
    };
  } catch (error) {
    return {
      type: CONFIRM_SHIPBACK_ADDRESS_REJECTED,
      payload: { ...error, error: true },
    };
  }
}

export async function updateShipBackAddresses(itemId, addresses) {
  const { address, shipping_center_address } = addresses;

  if (shipping_center_address) {
    return {
      type: UPDATE_SHIPBACK_ADDRESSES,
      payload: addresses,
    };
  }
  try {
    const { data } = await WorthyApi.getShippingCenterAddress(itemId, address);
    return {
      type: UPDATE_SHIPBACK_ADDRESSES,
      payload: {
        ...addresses,
        address,
        shipping_center_address: data.shipping_center_address,
      },
    };
  } catch (error) {
    return {
      type: UPDATE_SHIPBACK_ADDRESSES,
      payload: {
        ...addresses,
        address,
        shipping_center_address: null,
      },
    };
  }
}

export async function updateShipBackDestination(itemId, addresses) {
    const { shipping_option } = addresses;

    if (!shipping_option) {
        throw new Error('No shipping option provided');
    }

  return {
    type: UPDATE_SHIPBACK_DESTINATION,
    payload: addresses,
  }
}

export function clearItemAndCache(itemId) {
    if (itemId) {
        gettingItems[itemId] = false;
    }

    return {
        type:       ITEM,
        payload: {
            data: {
                item: null
            }
        }
    };
}

export function clearItemFromCache(item) {
    if (item && item.id) {
        gettingItems[item.id] = false;

        const updatedItem = { ...item, gettingItem: false }

        return {
            type:       ITEM,
            payload: {
                data: {
                    item: updatedItem
                }
            }
        };
    }
}

export function needsEstimation(item) {
    // make sure the current state warrents the estimation generation.
    if (item.item_type === 'watch' || !['validation_needed', 'submission'].includes(item.workflow_state)) {
        return false;    // Let the caller know we didn't generate an estimation
    }

    // check if the local storage key exists that marks estimation was generated.
    if (generateEstimationCallSent ||
        window.localStorage.getItem(ESTIMATION_GENERATED_PREFIX_KEY + '-' + item.id) === 'true') {
        return false;
    }
    return true;
}

export function generateEstimationIfNeeded(item) {
    if (!needsEstimation(item)) return false;

    // generate estimation and return true to notify the caller.
    rootStore.dispatch(generateEstimation(item.id));
    return true;
}

export function generateEstimation(id) {

    gettingItems[id] = true;
    generateEstimationCallSent = true;

    let request =
        WorthyApi.generateItemEstimationByPublicID(id)
            .then((response) => {
                gettingItems[id] = false;
                let item = response.data.item;
                if (item?.submission_qualification === 0 ) {
                    const user = response.data.user;
                    const { items_count, days_since_user_created, id: userId } = user;


                    /*  we only want to send FB & google (search & display) events on new users
                        new user is defined as a qualified first submission
                        items_count === 1
                        days_since_creation === 0 (this doesn't include JB - it's a different flow)
                        estimation is over the minimum threshold (1000$) - supported out of the box as rejected items don't get here
                        WORTHY-11437 - Currently We are optimizing for leads (amount of purchase events) so we want to use the same event as the main account rather than
                        creating a new pixel for the ML1 exp and send the value for the purchase event as:
                        estimation * item_score * factor (explanation on the factor is in the const description)
                    */
                    const isFirstSubmission = items_count === 1 && days_since_user_created <= 0;
                    const leadScore = item.prereg_score;
                    const isSuperExpensive = item.value_category === SUPER_EXPENSIVE;
                    let amount = item.estimation.amount;
                    let isNewUser = !!rootStore.getState().Register;
                    if (isFirstSubmission && !isSuperExpensive) {
                        isNewUser = 'first qualified submission - new lead';
                        //  TODO make this dynamic and returned as avg from P13N service
                        let fallback_score = 0.1083;
                        let factor = item.estimation.fb_ml1_exp_factor;
                        let score = leadScore || fallback_score;
                        amount = item.estimation.amount * score * factor;
                        GA.hqPurchase(score, item.estimation.amount, userId);
                        GA.ml4PurchaseEvent(amount * item.estimation.fb_ml4_prereg_score_weight);
                    }
                    GA.funnelStage( amount, { items_count, days_since_user_created, userId });
                }

                window.localStorage.setItem(ESTIMATION_GENERATED_PREFIX_KEY + '-' + id, 'true');
                rootStore.dispatch(hideLoader());
                return response;
            })
            .catch((error) => {
                gettingItems[id] = false;
                rootStore.dispatch(hideLoader());
                throw error;
            });

    return {
        type:       ITEM_ESTIMATION,
        payload:    request
    };
}

export function autoEstimateForCUI(id) {
    let request =
        WorthyApi.autoEstimateForCUI(id)
            .then((response) => {
                rootStore.dispatch(hideLoader());
                return response;
            })
            .catch((error) => {
                rootStore.dispatch(hideLoader());
                throw error;
            });

    return {
        type:       ITEM_ESTIMATION,
        payload:    request
    };
}


export function acceptItemEstimation(id, refreshState = true) {
    rootStore.dispatch(showLoader(false));
    let request =
        WorthyApi.acceptItemEstimationByPublicID(id)
            .then((res) => {
                rootStore.dispatch(hideLoader(false));
                return res;
            })
            .catch((error) => {
                rootStore.dispatch(hideLoader(false));
                throw error;
            });

    return {
        type:       (refreshState ? ITEM : null),
        payload:    request
    };
}

export function generateOfficeDropOffShipment(id, params, refreshItemState = true) {
    let request =
        WorthyApi.generateOfficeDropOffShipment(id, params)
            .then((res) => {
                return res;
            })
            .catch((error) => {
                throw error;
            });
    return {
        type: (refreshItemState ? SHIPPING_LABEL : ONLY_SHIPPING_LABEL),
        payload: request
    };
}

export function generateShippingLabel(id, params, refreshItemState = true) {
    rootStore.dispatch(showLoader(false));

    let request =
        WorthyApi.generateItemShippingByPublicID(id, params)
            .then((res) => {
                rootStore.dispatch(hideLoader(false));
                return res;
            })
            .catch((error) => {
                rootStore.dispatch(hideLoader(false));
                throw error;
            });

    return {
        type:       (refreshItemState ? SHIPPING_LABEL : ONLY_SHIPPING_LABEL),
        payload:    request
    };
}

export function startAuction(id, params, refreshState = false) {
    rootStore.dispatch(showLoader(false));
    let request =
        WorthyApi.startAuction(id, params)
            .then((res) => {
                rootStore.dispatch(hideLoader(false));
                return res;
            })
            .catch((error) => {
                rootStore.dispatch(hideLoader(false));
                throw error;
            });

    return {
        type:       (refreshState ? PENDING_AUCTION : null),
        payload:    request
    };
}

export function prepareStartAuction(id, params, refreshState = false) {
    rootStore.dispatch(showLoader(false));
    let request =
        WorthyApi.prepareStartAuction(id, params)
            .then((res) => {
                rootStore.dispatch(hideLoader(false));
                return res;
            })
            .catch((error) => {
                rootStore.dispatch(hideLoader(false));
                throw error;
            });

    return {
        type:       (refreshState ? PENDING_AUCTION : null),
        payload:    request
    };
}

export function acceptSell(id) {
    rootStore.dispatch(showLoader(false));
    let request =
        WorthyApi.acceptSell(id)
            .then((res) => {
                rootStore.dispatch(hideLoader(false));
                return res;
            })
            .catch((error) => {
                rootStore.dispatch(hideLoader(false));
                throw error;
            });

    return {
        type:       null,
        payload:    request
    };
}

export function setNegotiationPrice(id, price) {
    let request =
        WorthyApi.setNegotiationPrice(id, price)
            .then((res) => {
                rootStore.dispatch(hideLoader(false));
                return res;
            })
            .catch((error) => {
                rootStore.dispatch(hideLoader(false));
                throw error;
            });

    return {
        type:       null,
        payload:    request
    };
}

export function shipItemBack(id, params) {
    let request =
        WorthyApi.shipItemBack(id, params)
            .then((res) => {
                rootStore.dispatch(hideLoader(false));
                return res;
            })
            .catch((error) => {
                rootStore.dispatch(hideLoader(false));
                throw error;
            });

    return {
        type:       null,
        payload:    request
    };
}

export function setItemChatBot(id) {
    let request =
        WorthyApi.setItemChatBot(id);

    return {
        type:       null,
        payload:    request
    };
}

export function chatBotSaveLog(itemID, message, version) {
    let request =
        WorthyApi.cuiSaveLog(itemID, 'Item', message, version);

    return {
        type:       null,
        payload:    request
    };
}

export function chatBotJBSaveLog(itemID, message, version) {
    let request =
        WorthyApi.jewelryBoxSaveLog(itemID, version, message.key, message.text);

    return {
        type:       null,
        payload:    request
    };
}

export function highValueSaveLog(version, itemID, comment, notifySlack) {
    let request =
        WorthyApi.highValueSaveLog(version, itemID, comment, notifySlack);

    return {
        type:       null,
        payload:    request
    };
}

export function hvCuiSaveLog(itemID, version, message, lastKey, system, key = null) {
    key = key || message.toLowerCase().replace(/ /g, '_').replace(/'/g, '');
    WorthyApi.hvCuiSaveLog(itemID, version, key, message, lastKey, system);

    return {
        type: UPDATE_HV_CUI_LAST_MESSAGE_KEY,
        payload: key
    };
}

export function itemSaveLog(itemID, comment) {
    let request =
        WorthyApi.itemSaveLog(itemID, comment);

    return {
        type:       null,
        payload:    request
    };
}

export function updateBuyItNowPrice(itemID, price, message, version) {
    let request =
        WorthyApi.updateBuyItNowPrice(itemID, price, message, version);

    return {
        type:       null,
        payload:    request
    };
}

export function updateShipmentMethodDetails(itemID, taskSystemName) {
    let request =
        WorthyApi.createShipmentMethodDetailsTask(itemID, taskSystemName);

    return {
        type:       null,
        payload:    request
    };
}

/**
 *
 * @param bundleId
 * @param version --> Cui Version
 * @param key --> current key
 * @param message
 * @param system --> Boolean True/False system or user
 * @param to --> Table name in DB
 * @returns {{type: null, payload: *}}
 */
export function jewelryBoxSaveLog(bundleId, version, key, message, system, to = null) {
    let request =
        WorthyApi.jewelryBoxSaveLog(bundleId, version, key, message, system, to);

    return {
        type:       null,
        payload:    request
    };

}

/**
 *
 * @param bundleId
 * @param itemType
 * @returns {{type: null, payload: *}}
 */

export function addAnotherPiece(bundleId, itemType) {
    const request =
        WorthyApi.addDefaultItem(bundleId, itemType);

    return {
        type: ADDITIONAL_ITEM,
        payload: request
    };
}

